/**
 * Резолв сообщения API-ошибки по errorCode / error_params (после soykajs normalize).
 *
 * Порядок:
 * 1. api_errors.{code} — основной путь (оставить)
 * 2. legacy error_description — убрать после миграции бэкенда
 * 3. http.status.{status} — оставить (abort без error_code / пустой errors)
 * 4. http.errors[statusText]
 * 5. common.error / status — оставить как последний рубеж
 *
 * Сырой error_code без ключа в UI не показывается.
 */

import { i18n } from '@/i18n'

/**
 * Собирает человекочитаемое сообщение для ответа API / ApiError.
 *
 * @param {object} [options]
 * @param {string} [options.errorCode] стабильный domain.reason с бэкенда.
 * @param {object} [options.errorParams] параметры интерполяции.
 * @param {string} [options.errorDescription] legacy-текст из error_description.
 * @param {number} [options.status] HTTP status.
 * @param {string} [options.statusText] statusText ответа (fallback через http.errors).
 * @returns {string} текст для toast / локального catch.
 */
export function resolveApiErrorMessage(options = {}) {
  const { errorCode, errorParams, errorDescription, status, statusText } = options
  const t = i18n.global.t.bind(i18n.global)
  const te = i18n.global.te.bind(i18n.global)

  if (errorCode) {
    const key = `api_errors.${errorCode}`
    if (te(key)) {
      const params = {
        detail: '',
        ...(errorParams && typeof errorParams === 'object' ? errorParams : {}),
      }
      return t(key, params)
    }
  }

  // --- legacy (ltms-backend): убрать блок, когда в JSON нет error_description ---
  // (и строку errorDescription в api.js; на бэке — errors.py).
  const legacyDescription = typeof errorDescription === 'string' ? errorDescription.trim() : ''
  if (legacyDescription && legacyDescription.toUpperCase() !== 'BAD REQUEST') {
    console.warn(
      'WARN: legacy error_description — убрать этот fallback, если в кодовой базе нет ' +
        'abort(..., description=) / BadRequest(description=) / error_description. ' +
        'Пример чего нет: description="…", error_description в JSON. Получено:',
      legacyDescription
    )
    return legacyDescription
  }
  // --- /legacy ---

  // abort(N) без error_code → http.status.N (оставить)
  if (status != null && te(`http.status.${status}`)) {
    return t(`http.status.${status}`)
  }

  if (statusText) {
    const httpErrors = i18n.global.tm('http.errors')
    if (httpErrors && typeof httpErrors === 'object' && statusText in httpErrors) {
      return httpErrors[statusText]
    }
  }

  // последний рубеж: неизвестный код / не-HTTP (оставить te/t)
  if (status != null) {
    return String(status)
  }

  return te('common.error') ? t('common.error') : 'Error'
}

/**
 * Возвращает причину отказа для уведомления о неудавшемся действии.
 * API-ошибки уже локализованы в useApi; сетевые ошибки переводим отдельно.
 *
 * @param {object|string|null} error ошибка из обработчика действия.
 * @param {string} actionMessage общее описание неудавшегося действия.
 * @returns {string} причина ошибки или сообщение об отсутствии подробностей.
 */
export function resolveActionErrorReason(error, actionMessage) {
  const message = typeof error === 'string' ? error : error?.message
  const reason = typeof message === 'string' ? message.trim() : ''

  if (/^(Failed to fetch|fetch failed|Load failed|NetworkError when attempting to fetch resource\.?)$/i.test(reason)) {
    return i18n.global.t('common.networkErrorReason')
  }
  if (reason && reason !== actionMessage) {
    return reason
  }
  if (error?.status != null) {
    return resolveApiErrorMessage({ status: error.status })
  }
  return i18n.global.t('common.unknownErrorReason')
}

/**
 * Строит flat-ключ Form из loc (сегменты пути).
 *
 * @param {Array} loc сегменты пути поля.
 * @returns {string|null}
 */
export function locToFieldKey(loc) {
  if (!Array.isArray(loc) || !loc.length) {
    return null
  }
  return loc.map(String).join('.')
}

/**
 * Маппит пункты ApiError.errors с loc в карту field → параметры ошибки.
 *
 * @param {object} apiError экземпляр ApiError (или объект с .errors / .status).
 * @returns {Record<string, object>|null} карта или null, если нечего применить.
 */
export function mapApiErrorsToFields(apiError) {
  // ApiError.errors уже нормализован в soykajs (errorCode / errorParams / loc)
  const items = apiError?.errors
  if (!Array.isArray(items) || !items.length) {
    return null
  }

  const fieldMap = {}
  let hasAny = false

  for (const item of items) {
    const key = locToFieldKey(item.loc)
    if (!key) {
      continue
    }
    fieldMap[key] = {
      errorCode: item.errorCode,
      errorParams: item.errorParams,
      status: apiError.status,
    }
    hasAny = true
  }

  return hasAny ? fieldMap : null
}
